import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";

/*
 * Copyright (C) 2017 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that they will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */
import React, { Component } from 'react';
import { themeable } from '@instructure/ui-themeable';
import classnames from 'classnames';
import { partition } from 'lodash';
import { arrayOf, string, number, shape, func } from 'prop-types';
import moment from 'moment-timezone';
import { userShape, itemShape, sizeShape } from '../plannerPropTypes';
const styles = {
  componentId: 'eOFoE',
  template: function template(theme) {
    return "\n\n\n\n.Grouping-styles__root {\n  font-family: ".concat(theme.fontFamily || 'inherit', ";\n  margin: ").concat(theme.margin || 'inherit', ";\n  border-color: ").concat(theme.groupColor || 'inherit', ";\n  color: ").concat(theme.groupColor || 'inherit', ";\n  line-height: ").concat(theme.lineHeight || 'inherit', ";\n  position: relative;\n  display: flex;\n}\n\n.Grouping-styles__title {\n  position: relative;\n  z-index: 1;\n  flex: 1;\n  box-sizing: border-box;\n  text-align: center;\n  padding: ").concat(theme.titlePadding || 'inherit', ";\n  background-color: ").concat(theme.titleBackground || 'inherit', ";\n  text-transform: ").concat(theme.titleTextTransform || 'inherit', ";\n  -webkit-text-decoration: ").concat(theme.titleTextDecoration || 'inherit', ";\n          text-decoration: ").concat(theme.titleTextDecoration || 'inherit', ";\n  font-size: ").concat(theme.titleFontSize || 'inherit', ";\n  font-weight: ").concat(theme.titleFontWeight || 'inherit', ";\n  color: ").concat(theme.titleColor || 'inherit', ";\n\n  \n  min-width: 1px;\n  overflow: hidden;\n  max-height: 3rem;\n  overflow-wrap: break-word;\n  word-wrap: break-word;\n  -webkit-hyphens: auto;\n      -ms-hyphens: auto;\n          hyphens: auto;\n}\n\n[dir=\"ltr\"] .Grouping-styles__title {\n  text-align: center;\n}\n\n[dir=\"rtl\"] .Grouping-styles__title {\n  text-align: center;\n}\n\n.Grouping-styles__title::after {\n    content: \"\";\n    width: 100%;\n    height: ").concat(theme.titleOverflowGradientHeight || 'inherit', ";\n    position: absolute;\n    bottom: 0;\n    left: 0;\n    background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, ").concat(theme.titleBackground || 'inherit', " 100%);\n  }\n\n.Grouping-styles__hero {\n  position: relative;\n  display: flex;\n  flex: 0 0 ").concat(theme.heroWidth || 'inherit', ";\n  background-repeat: no-repeat;\n  background-position: center center;\n  background-size: cover;\n  align-items: center;\n  justify-content: center;\n  box-sizing: border-box;\n  outline: none;\n  padding: ").concat(theme.heroPadding || 'inherit', ";\n  text-decoration: none;\n\n  \n  min-width: 1px;\n}\n\n.Grouping-styles__hero .Grouping-styles__groupingName {\n    -webkit-text-decoration: ").concat(theme.heroLinkTextDecoration || 'inherit', ";\n            text-decoration: ").concat(theme.heroLinkTextDecoration || 'inherit', ";\n  }\n\n.Grouping-styles__hero,\n.Grouping-styles__overlay {\n  border-bottom-inline-start-radius: ").concat(theme.heroBorderRadius || 'inherit', ";\n  border-top-inline-start-radius: ").concat(theme.heroBorderRadius || 'inherit', ";\n}\n\n[dir=\"ltr\"] .Grouping-styles__hero,\n[dir=\"ltr\"] .Grouping-styles__overlay {\n  border-bottom-left-radius: ").concat(theme.heroBorderRadius || 'inherit', ";\n  border-top-left-radius: ").concat(theme.heroBorderRadius || 'inherit', ";\n}\n\n[dir=\"rtl\"] .Grouping-styles__hero,\n[dir=\"rtl\"] .Grouping-styles__overlay {\n  border-bottom-right-radius: ").concat(theme.heroBorderRadius || 'inherit', ";\n  border-top-right-radius: ").concat(theme.heroBorderRadius || 'inherit', ";\n}\n\n.Grouping-styles__heroHover:focus,\n  .Grouping-styles__heroHover:hover {\n    text-decoration: none;\n  }\n\n.Grouping-styles__heroHover:focus .Grouping-styles__title, .Grouping-styles__heroHover:hover .Grouping-styles__title {\n      -webkit-text-decoration: ").concat(theme.titleTextDecorationHover || 'inherit', ";\n              text-decoration: ").concat(theme.titleTextDecorationHover || 'inherit', ";\n    }\n\n.Grouping-styles__overlay {\n  background-color: ").concat(theme.groupColor || 'inherit', ";\n  opacity: 1;\n  width: 100%;\n  height: 100%;\n  position: absolute;\n  top: 0;\n  left: 0;\n}\n\n.Grouping-styles__overlay.Grouping-styles__withImage {\n    opacity: ").concat(theme.overlayOpacity || 'inherit', ";\n  }\n\n\n.Grouping-styles__items {\n  flex: 1;\n  list-style-type: none;\n  margin: 0;\n  padding: 0;\n  border-top: ").concat(theme.borderTopWidth || 'inherit', " solid;\n  border-color: ").concat(theme.groupColor || 'inherit', ";\n  color: ").concat(theme.groupColor || 'inherit', ";\n  min-width: 1px;\n}\n\n.Grouping-styles__medium.Grouping-styles__root, .Grouping-styles__small.Grouping-styles__root {\n    display: block;\n    margin: 0;\n  }\n\n.Grouping-styles__medium .Grouping-styles__hero, .Grouping-styles__medium .Grouping-styles__overlay, .Grouping-styles__small .Grouping-styles__hero, .Grouping-styles__small .Grouping-styles__overlay {\n    border-radius: 0;\n    background-color: transparent;\n  }\n\n.Grouping-styles__medium .Grouping-styles__hero, .Grouping-styles__small .Grouping-styles__hero {\n    display: block;\n    flex: none;\n    min-height: unset;\n    line-height: 2rem;\n  }\n\n.Grouping-styles__medium .Grouping-styles__title, .Grouping-styles__small .Grouping-styles__title {\n    font-size: ").concat(theme.titleFontSizeTablet || 'inherit', ";\n    -webkit-padding-start: 0;\n            padding-inline-start: 0;\n  }\n\n[dir=\"ltr\"] .Grouping-styles__medium .Grouping-styles__title,\n[dir=\"ltr\"] .Grouping-styles__small .Grouping-styles__title {\n    padding-left: 0;\n  }\n\n[dir=\"rtl\"] .Grouping-styles__medium .Grouping-styles__title,\n[dir=\"rtl\"] .Grouping-styles__small .Grouping-styles__title {\n    padding-right: 0;\n  }\n\n.Grouping-styles__medium .Grouping-styles__items, .Grouping-styles__small .Grouping-styles__items {\n    border-top-width: ").concat(theme.borderTopWidthTablet || 'inherit', ";\n  }\n");
  },
  'root': 'Grouping-styles__root',
  'title': 'Grouping-styles__title',
  'hero': 'Grouping-styles__hero',
  'groupingName': 'Grouping-styles__groupingName',
  'overlay': 'Grouping-styles__overlay',
  'heroHover': 'Grouping-styles__heroHover',
  'withImage': 'Grouping-styles__withImage',
  'items': 'Grouping-styles__items',
  'medium': 'Grouping-styles__medium',
  'small': 'Grouping-styles__small'
};
import theme from './theme.js';
import PlannerItem from '../PlannerItem';
import CompletedItemsFacade from '../CompletedItemsFacade';
import NotificationBadge, { MissingIndicator, NewActivityIndicator } from '../NotificationBadge';
import formatMessage from '../../format-message';
import { getBadgesForItem, getBadgesForItems, showPillForOverdueStatus } from '../../utilities/statusUtils';
import { animatable } from '../../dynamic-ui';
import responsiviser from '../responsiviser';
export class Grouping extends Component {
  constructor(props) {
    super(props);

    this.groupingLinkRef = link => {
      this.groupingLink = link;
    };

    this.getFocusable = () => {
      return this.groupingLink;
    };

    this.handleFacadeClick = e => {
      if (e) {
        e.preventDefault();
      }

      this.setState(() => ({
        showCompletedItems: true
      }), () => {
        if (this.groupingLink) this.groupingLink.focus();
      });
    };

    this.state = {
      showCompletedItems: false,
      badgeMap: this.setupItemBadgeMap(props.items)
    };
  }

  componentDidMount() {
    this.props.registerAnimatable('group', this, this.props.animatableIndex, this.itemUniqueIds());
  }

  componentWillReceiveProps(newProps) {
    this.props.deregisterAnimatable('group', this, this.itemUniqueIds());
    this.props.registerAnimatable('group', this, newProps.animatableIndex, this.itemUniqueIds(newProps));
  }

  componentWillUnmount() {
    this.props.deregisterAnimatable('group', this, this.itemUniqueIds());
  }

  itemUniqueIds() {
    let props = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : this.props;
    return props.items.map(item => item.uniqueId);
  }

  setupItemBadgeMap(items) {
    const mapping = {};
    items.forEach(item => {
      const badges = getBadgesForItem(item);
      if (badges.length) mapping[item.id] = badges;
    });
    return mapping;
  }

  getScrollable() {
    return this.groupingLink || this.plannerNoteHero;
  }

  getLayout() {
    return this.props.responsiveSize;
  }

  renderItemsAndFacade(items) {
    const _partition = partition(items, item => item.completed && !item.show),
          _partition2 = _slicedToArray(_partition, 2),
          completedItems = _partition2[0],
          otherItems = _partition2[1];

    let itemsToRender = otherItems;

    if (this.state.showCompletedItems) {
      itemsToRender = items;
    }

    const componentsToRender = this.renderItems(itemsToRender);
    componentsToRender.push(this.renderFacade(completedItems, this.props.animatableIndex * 100 + itemsToRender.length + 1));
    return componentsToRender;
  }

  renderItems(items) {
    const showNotificationBadgeOnItem = this.getLayout() !== 'large';
    return items.map((item, itemIndex) => React.createElement("li", {
      className: styles.item,
      key: item.uniqueId
    }, React.createElement(PlannerItem, {
      theme: {
        iconColor: this.props.color
      },
      color: this.props.color,
      completed: item.completed,
      overrideId: item.overrideId,
      id: item.id,
      uniqueId: item.uniqueId,
      animatableIndex: this.props.animatableIndex * 100 + itemIndex + 1,
      courseName: this.props.title,
      context: item.context || {},
      date: moment(item.date).tz(this.props.timeZone),
      associated_item: item.type,
      title: item.title,
      points: item.points,
      updateTodo: this.props.updateTodo,
      html_url: item.html_url,
      toggleCompletion: () => this.props.toggleCompletion(item),
      badges: this.state.badgeMap[item.id],
      details: item.details,
      toggleAPIPending: item.toggleAPIPending,
      status: item.status,
      newActivity: item.newActivity,
      allDay: item.allDay,
      showNotificationBadge: showNotificationBadgeOnItem,
      currentUser: this.props.currentUser,
      feedback: item.feedback,
      location: item.location,
      address: item.address,
      endTime: item.endTime,
      dateStyle: item.dateStyle,
      timeZone: this.props.timeZone
    })));
  }

  renderFacade(completedItems, animatableIndex) {
    const showNotificationBadgeOnItem = this.getLayout() !== 'large';

    if (!this.state.showCompletedItems && completedItems.length > 0) {
      const theDay = completedItems[0].date.clone();
      theDay.startOf('day');
      let missing = false;
      let newActivity = false;
      const completedItemIds = completedItems.map(item => {
        if (showPillForOverdueStatus('missing', item)) missing = true;
        if (item.newActivity) newActivity = true;
        return item.uniqueId;
      });
      let notificationBadge = 'none';

      if (showNotificationBadgeOnItem) {
        if (newActivity) {
          notificationBadge = 'newActivity';
        } else if (missing) {
          notificationBadge = 'missing';
        }
      }

      return React.createElement("li", {
        className: styles.item,
        key: "completed"
      }, React.createElement(CompletedItemsFacade, {
        onClick: this.handleFacadeClick,
        itemCount: completedItems.length,
        badges: getBadgesForItems(completedItems),
        animatableIndex: animatableIndex,
        animatableItemIds: completedItemIds,
        notificationBadge: notificationBadge,
        theme: {
          labelColor: this.props.color
        },
        date: theDay,
        responsiveSize: this.props.responsiveSize
      }));
    }

    return null;
  }

  renderToDoText() {
    return formatMessage('To Do');
  }

  renderNotificationBadge() {
    // narrower layout puts the indicator next to the actual items
    if (this.getLayout() !== 'large') {
      return null;
    }

    let missing = false;
    const newItem = this.props.items.find(item => {
      if (showPillForOverdueStatus('missing', item)) missing = true;
      return item.newActivity;
    });

    if (newItem || missing) {
      const IndicatorComponent = newItem ? NewActivityIndicator : MissingIndicator;
      const badgeMessage = this.props.title ? this.props.title : this.renderToDoText();
      return React.createElement(NotificationBadge, null, React.createElement(IndicatorComponent, {
        title: badgeMessage,
        itemIds: this.itemUniqueIds(),
        animatableIndex: this.props.animatableIndex,
        getFocusable: this.getFocusable
      }));
    } else {
      return React.createElement(NotificationBadge, null);
    }
  } // I wouldn't have broken the background and title apart, but wrapping them in a container span breaks styling


  renderGroupLinkBackground() {
    const clazz = classnames({
      [styles.overlay]: true,
      [styles.withImage]: this.props.image_url
    });
    const style = this.getLayout() === 'large' ? {
      backgroundColor: this.props.color
    } : null;
    return React.createElement("span", {
      className: clazz,
      style: style
    });
  }

  renderGroupLinkTitle() {
    return React.createElement("span", {
      className: styles.title
    }, this.props.title || this.renderToDoText());
  }

  renderGroupLink() {
    if (!this.props.title) {
      return React.createElement("span", {
        className: styles.hero,
        ref: elt => this.plannerNoteHero = elt
      }, this.renderGroupLinkBackground(), this.renderGroupLinkTitle());
    }

    const style = this.getLayout() === 'large' ? {
      backgroundImage: "url(".concat(this.props.image_url || '', ")")
    } : null;
    return React.createElement("a", {
      href: this.props.url || '#',
      ref: this.groupingLinkRef,
      className: "".concat(styles.hero, " ").concat(styles.heroHover),
      style: style
    }, this.renderGroupLinkBackground(), this.renderGroupLinkTitle());
  }

  render() {
    return React.createElement("div", {
      className: classnames(styles.root, styles[this.getLayout()], 'planner-grouping')
    }, this.renderNotificationBadge(), this.renderGroupLink(), React.createElement("ol", {
      className: styles.items,
      style: {
        borderColor: this.props.color
      }
    }, this.renderItemsAndFacade(this.props.items)));
  }

}
Grouping.propTypes = {
  items: arrayOf(shape(itemShape)).isRequired,
  animatableIndex: number,
  title: string,
  color: string,
  image_url: string,
  timeZone: string.isRequired,
  url: string,
  toggleCompletion: func,
  updateTodo: func,
  registerAnimatable: func,
  deregisterAnimatable: func,
  currentUser: shape(userShape),
  responsiveSize: sizeShape
};
Grouping.defaultProps = {
  registerAnimatable: () => {},
  deregisterAnimatable: () => {},
  responsiveSize: 'large'
};
const ResponsiveGrouping = responsiviser()(Grouping);
export default animatable(themeable(theme, styles)(ResponsiveGrouping));